import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FocusLock from 'react-focus-lock';
import Button from '../../atoms/Button/Button';
import Input from '../../atoms/Input/Input';
import { searchValidationConfig } from '../../../types/ValidationTypes';
import { searchLogs } from '../../../api/logs/logs';
import { useLogsStore } from '../../../store/logs/logsStore';

import { useAppState } from '../../../store/appstate';
import {
    createEncodedSearchQueryString,
    parseSearchUrl,
    removeQuoteSearchFromSearchPhrase,
    generateQuoteSearchEscapeQuotesInContent,
} from '../../../services/helpers/queryStringHelpers';
import { parseFilters } from '../../../services/helpers/filterHelpers';
import CheckBox from '../../atoms/Checkbox/Checkbox';
import { setPerPage } from '../../../store/userSettings/userSettingsSlice';
import { useAppDispatch } from '../../../store';
import FilterDropdown from '../../atoms/Dropdown/FilterDropdown';
import SearchFilterButton from '../../atoms/Button/SearchFilterButton';
import useModal from '../../../services/hooks/useModal';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { useTheme } from 'styled-components';
import DateRange from '../../atoms/DateRange/DateRange';
import RelativeOperationTypes from '../../../types/RelativeOperation';
import Flex from '../../atoms/Box/Flex';

type Inputs = {
    searchPhrase: string;
    filter: string[];
};

interface SearchFormProps {
    organizationId?: string;
}

const STATUS_OPTIONS = [
    '200',
    '201',
    '202',
    '400',
    '401',
    '403',
    '404',
    '409',
    '422',
    '423',
    '500',
    '503',
];
const METHOD_OPTIONS = ['GET', 'POST', 'PATCH', 'DELETE'];

const FILTER_KEY_STATUS = 'Properties.StatusCode_i';
const FILTER_KEY_METHOD = 'Properties.RequestMethod_s';
const FILTER_KEY_TIMESTAMP = 'Timestamp';

const today = new Date();

const relativeTimestampRanges = [
    { name: 'today', label: 'Today', value: '0d' },
    { name: 'yesterday', label: 'Yesterday', value: 'yesterday' },
    { name: 'last7days', label: 'Last 7 days', value: '-7d' },
    { name: 'last30days', label: 'Last 30 days', value: '-30d' },
    {
        name: 'custom',
        label: 'Custom',
        value: '-1d',
    },
];

export const SearchForm: React.FC<SearchFormProps> = ({ organizationId }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
    } = useForm<Inputs>();

    const {
        isSearching,
        searchCancelToken,
        exactSearch,
        searchStarted,
        searchSucceeded,
        searchFailed,
        searchCanceled,
        setExactSearch,
    } = useLogsStore();

    const logColumns = useAppState<string[]>(s => s.userSettings.logColumnFilters);
    const { pathname, search } = useLocation();
    const { page, sort, query, columns, perPage, filterMap } = parseSearchUrl(search, logColumns);

    const statusModal = useModal();
    const methodModal = useModal();

    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
    const [selectedMethods, setSelectedMethods] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSelectedStatuses(new Set(parseFilters(FILTER_KEY_STATUS, filterMap)));
        setSelectedMethods(new Set(parseFilters(FILTER_KEY_METHOD, filterMap)));
    }, [search]);

    const SEARCH_PHRASE = 'searchPhrase';
    const searchPhrase = register(SEARCH_PHRASE, searchValidationConfig);

    useEffect(() => {
        performSearch(query, exactSearch, page, false);
    }, [page]);

    useEffect(() => {
        if (organizationId) {
            performSearch(query, exactSearch, '1', false);
        }
    }, [organizationId]);

    const onSubmit = (form: Inputs) => {
        performSearch(form.searchPhrase, exactSearch, form.searchPhrase === query ? page : '1');
    };

    const onSetExactMatch = () => {
        const searchPhrase = getValues(SEARCH_PHRASE);
        setExactSearch(!exactSearch);
        performSearch(searchPhrase, !exactSearch, '1');
    };

    const handleFilterChange = (key: string | undefined, values: string[]) => {
        if (!key) return;
        filterMap.set(key, values);
        performSearch(getValues(SEARCH_PHRASE), exactSearch, '1');
    };

    const onStatusChange = (e: HTMLInputElement) => {
        const next = new Set(selectedStatuses);
        if (e.checked) {
            next.add(e.value);
        } else {
            next.delete(e.value);
        }
        setSelectedStatuses(next);
        handleFilterChange(FILTER_KEY_STATUS, Array.from(next));
    };

    const onClearStatuses = () => {
        setSelectedStatuses(new Set());
        handleFilterChange(FILTER_KEY_STATUS, []);
        if (statusModal.isShown) statusModal.toggle();
    };

    const onMethodChange = (e: HTMLInputElement) => {
        const next = new Set(selectedMethods);
        if (e.checked) {
            next.add(e.value);
        } else {
            next.delete(e.value);
        }
        setSelectedMethods(next);
        handleFilterChange(FILTER_KEY_METHOD, Array.from(next));
    };

    const onClearMethods = () => {
        setSelectedMethods(new Set());
        handleFilterChange(FILTER_KEY_METHOD, []);
        if (methodModal.isShown) methodModal.toggle();
    };

    const clearAllFilters = () => {
        setSelectedStatuses(new Set());
        setSelectedMethods(new Set());
        filterMap.clear();
        setValue(SEARCH_PHRASE, '');
        performSearch('', exactSearch, '1');
    };

    const getFilterLabel = (selected: Set<string>, allLabel = 'All') => {
        if (selected.size === 0) return allLabel;
        if (selected.size === 1) return selected.values().next().value;
        return selected.size;
    };

    useEffect(() => {
        dispatch(setPerPage(perPage));
    }, [dispatch]);

    const performSearch = (query: string, exact: boolean, p: string, nav: boolean = true) => {
        if (isSearching && searchCancelToken) searchCancelToken.cancel();
        const withOrWithoutQuotes = exact
            ? generateQuoteSearchEscapeQuotesInContent(query)
            : removeQuoteSearchFromSearchPhrase(query);

        const newQuery = createEncodedSearchQueryString(
            withOrWithoutQuotes,
            filterMap,
            sort,
            perPage,
            columns,
            p
        );

        if (nav) {
            navigate(`${pathname}?${newQuery}`);
        }

        searchLogs(
            newQuery,
            searchStarted,
            searchSucceeded,
            searchFailed,
            searchCanceled,
            organizationId || undefined
        );
    };

    return (
        <Flex column gap={theme.layout.gap.xsmall}>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="form">
                <SearchContainer>
                    <InputWrapper>
                        <FocusLock disabled={!isSearching}>
                            <Input
                                data-testid="search-input"
                                type="text"
                                name={searchPhrase.name}
                                defaultValue={removeQuoteSearchFromSearchPhrase(query)}
                                setRef={searchPhrase.ref}
                                placeholder="What are you looking for?"
                                onChange={searchPhrase.onChange}
                                id="log-search"
                            />
                        </FocusLock>
                    </InputWrapper>
                    <Button type="submit" primary large>
                        Search
                    </Button>
                    <CheckBox
                        dataTestId="logsExactMatch"
                        checked={exactSearch}
                        onChange={onSetExactMatch}
                    >
                        Exact match
                    </CheckBox>
                </SearchContainer>
            </form>
            <FilterRow>
                <DateRange
                    maxDate={today}
                    label="Timestamp"
                    onDateChange={handleFilterChange}
                    initialValue={parseFilters(FILTER_KEY_TIMESTAMP, filterMap)}
                    dateRangeType={FILTER_KEY_TIMESTAMP}
                    relativeRanges={relativeTimestampRanges}
                    relativeLabel={{ type: 'Last', operation: RelativeOperationTypes.SUBTRACT }}
                />

                <FilterDropdown
                    toggle={statusModal.toggle}
                    isShown={statusModal.isShown}
                    initiator={
                        <SearchFilterButton
                            modalToggled={statusModal.isShown}
                            onClick={statusModal.toggle}
                            onClear={onClearStatuses}
                            filterExists={selectedStatuses.size > 0}
                            criteria="Status"
                        >
                            {getFilterLabel(selectedStatuses)}
                        </SearchFilterButton>
                    }
                >
                    <DropdownContent>
                        {STATUS_OPTIONS.map(status => (
                            <CheckBox
                                key={status}
                                dataTestId={`status-filter-${status}`}
                                id={`status-${status}`}
                                value={status}
                                name="statusCode"
                                checked={selectedStatuses.has(status)}
                                onChange={e => onStatusChange(e.target)}
                                noMargin
                            >
                                {status}
                            </CheckBox>
                        ))}
                    </DropdownContent>
                </FilterDropdown>

                <FilterDropdown
                    toggle={methodModal.toggle}
                    isShown={methodModal.isShown}
                    initiator={
                        <SearchFilterButton
                            modalToggled={methodModal.isShown}
                            onClick={methodModal.toggle}
                            onClear={onClearMethods}
                            filterExists={selectedMethods.size > 0}
                            criteria="Method"
                        >
                            {getFilterLabel(selectedMethods)}
                        </SearchFilterButton>
                    }
                >
                    <DropdownContent>
                        {METHOD_OPTIONS.map(method => (
                            <CheckBox
                                key={method}
                                dataTestId={`method-filter-${method}`}
                                id={`method-${method}`}
                                value={method}
                                name="httpMethod"
                                checked={selectedMethods.has(method)}
                                onChange={e => onMethodChange(e.target)}
                                noMargin
                            >
                                {method}
                            </CheckBox>
                        ))}
                    </DropdownContent>
                </FilterDropdown>

                <ClearAllButton
                    title="Clear all filters"
                    data-testid="search-clear-all-button"
                    onClick={() => clearAllFilters()}
                    disabled={filterMap.size === 0 && getValues(SEARCH_PHRASE) === ''}
                >
                    <CloseLineIcon size={theme.icon.size.small} />
                </ClearAllButton>
            </FilterRow>
            {errors.searchPhrase && (
                <p className="error-message" role="alert">
                    {errors.searchPhrase?.message}
                </p>
            )}
        </Flex>
    );
};

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.layout.gap.small};
`;

const InputWrapper = styled.div`
    width: 40rem;
`;

const FilterRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.layout.gap.xsmall};
    flex-wrap: wrap;
`;

const DropdownContent = styled.div`
    padding: 1.5rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 12rem;
`;

const ClearAllButton = styled.button`
    display: flex;
    align-items: center;
    background: ${props => props.theme.colors.button.light};
    color: ${props => props.theme.colors.black};
    border-radius: 0.5rem;
    border: none;

    &:disabled {
        opacity: 0.5;
    }

    &:hover:enabled {
        background: ${props => props.theme.colors.black};
        color: ${props => props.theme.colors.light};
        transition: all 0.6s;
        cursor: pointer;
    }
`;
